const CACHE_NAME = 'clue200-media-v1';
const INDEX_KEY = 'clue200-cache-index-v1';
const MAX_CACHE_BYTES = 200 * 1024 * 1024;
const EVICTION_THRESHOLD_BYTES = 180 * 1024 * 1024;
const activeKeys = new Set<string>();

interface CacheEntry {
  key: string;
  size: number;
  lastAccessed: number;
}

const readIndex = (): CacheEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(INDEX_KEY) || '[]') as CacheEntry[];
  } catch {
    return [];
  }
};

const writeIndex = (entries: CacheEntry[]) => {
  localStorage.setItem(INDEX_KEY, JSON.stringify(entries));
};

const cacheSupported = () => typeof window !== 'undefined' && 'caches' in window;

export const enforceCacheLimit = async (): Promise<void> => {
  if (!cacheSupported()) return;
  const cache = await caches.open(CACHE_NAME);
  let entries = readIndex().sort((a, b) => a.lastAccessed - b.lastAccessed);
  let total = entries.reduce((sum, entry) => sum + entry.size, 0);
  if (total <= EVICTION_THRESHOLD_BYTES) return;

  while (total > EVICTION_THRESHOLD_BYTES && entries.length > 0) {
    const activeIndex = entries.findIndex(entry => !activeKeys.has(entry.key));
    if (activeIndex < 0) break;
    const oldest = entries.splice(activeIndex, 1)[0];
    if (!oldest) break;
    await cache.delete(oldest.key);
    total -= oldest.size;
  }
  writeIndex(entries);
};

export const cacheMediaUrl = async (url?: string): Promise<void> => {
  // Firebase Storage download URLs are cross-origin and may not expose CORS
  // headers. Let the browser/WebView HTTP cache handle them instead of issuing
  // a fetch that would turn a valid image URL into a CORS failure.
  if (!url || !cacheSupported() || !url.startsWith(window.location.origin)) return;
  const cache = await caches.open(CACHE_NAME);
  const existing = await cache.match(url);
  const now = Date.now();
  const entries = readIndex();
  const known = entries.find(entry => entry.key === url);

  if (existing) {
    if (known) {
      known.lastAccessed = now;
      writeIndex(entries);
    }
    return;
  }

  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return;
    const copy = response.clone();
    const blob = await response.blob();
    await cache.put(url, copy);
    const next = entries.filter(entry => entry.key !== url);
    next.push({ key: url, size: blob.size, lastAccessed: now });
    writeIndex(next);
    await enforceCacheLimit();
  } catch {
    // A cache miss must never block feed rendering or media playback.
  }
};

export const markCacheActive = (url?: string): void => {
  if (url) activeKeys.add(url);
};

export const releaseCacheActive = (url?: string): void => {
  if (url) activeKeys.delete(url);
};

export const cacheJson = async <T>(key: string, value: T): Promise<void> => {
  try {
    localStorage.setItem(`clue200-feed-${key}`, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    // Feed caching is best effort and must not affect the live listener.
  }
};

export const readCachedJson = <T>(key: string): T | null => {
  try {
    const saved = JSON.parse(localStorage.getItem(`clue200-feed-${key}`) || 'null') as { value?: T } | null;
    return saved?.value || null;
  } catch {
    return null;
  }
};
